import React, { useState, Component } from 'react';
import { Calendar, MapPin, User, Mail, Package, Home, ArrowRight, Truck, X } from 'lucide-react';
import { Booking } from '../types/booking';
import { motion } from 'framer-motion';
import { HandoverModal } from './HandoverModal';
import { ReturnModal } from './ReturnModal';
interface BookingCardProps {
  booking: Booking;
  index: number;
  onExtendBooking?: () => void;
}
export function BookingCard({
  booking,
  index,
  onExtendBooking
}: BookingCardProps) {
  const [isHandoverModalOpen, setIsHandoverModalOpen] = useState(false);
  const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);
  const [isPickupDropoffOpen, setIsPickupDropoffOpen] = useState(false);
  const isCompleted = booking.status === 'completed';
  const statusColors = {
    active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    upcoming: 'bg-blue-50 text-blue-700 border-blue-100',
    completed: 'bg-slate-100 text-slate-500 border-slate-200',
    maintenance: 'bg-orange-50 text-orange-700 border-orange-100'
  };
  // Keep DD/MM/YYYY format as-is from the data
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    // If already in DD/MM/YYYY format, return as-is
    if (dateStr.includes('/')) {
      return dateStr;
    }
    // Otherwise convert from other formats
    return new Date(dateStr).toLocaleDateString('en-AU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };
  // Format handover text to use "from" instead of "at"
  const formatHandoverText = (text: string | undefined) => {
    if (!text) return '';
    // Replace "at" with "from" for handover (e.g., "Iara Sahsha at Sahsa Farms" → "Iara Sahsha from Sahsa Farms")
    return text.replace(' at ', ' from ');
  };
  // Get farm name or fallback to location
  const farmName = booking.farm || booking.location || 'Farm';
  // Get initials from farm name for avatar
  const getInitials = (name: string) => {
    const words = name.split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };
  // Generate extend or cancel email
  const handleContactCouncil = (e: React.MouseEvent) => {
    e.stopPropagation();
    const subject = `Booking Request - Booking #${booking.id} - ${farmName}`;
    const body = `Hi Team,

I would like to request a change to my booking:

Booking Details:
- Booking ID: ${booking.id}
- Farm: ${farmName}
- Farmer: ${booking.userName}
- Start Date: ${booking.startDate}
- End Date: ${booking.endDate}
- Location: ${booking.location}

Request Type (please select one):
[ ] Extension - I would like to extend my booking
[ ] Cancellation - I would like to cancel my booking

Details:
[Please describe your request here]

Best regards,
${booking.userName}`;
    window.location.href = `mailto:agplastic.SDRC@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };
  // Handle handover modal
  const handleViewHandover = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsHandoverModalOpen(true);
  };
  // Handle return modal
  const handleViewReturn = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsReturnModalOpen(true);
  };
  // Handle pickup/dropoff modal
  const handleViewPickupDropoff = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsPickupDropoffOpen(true);
  };
  // Show contact action for active and upcoming bookings only (not completed)
  const showContactAction = booking.status === 'active' || booking.status === 'upcoming';
  // Show next action for ACTIVE bookings only (not completed)
  const showNextAction = booking.status === 'active' && booking.nextAction && booking.nextAction !== 'none';
  const isHandover = booking.nextAction === 'handover';
  const isReturn = booking.nextAction === 'return-to-wmf';
  // Show pickup/dropoff info for upcoming bookings only
  const showPickupDropoff = booking.status === 'upcoming';
  return <>
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: index * 0.05,
      duration: 0.3
    }} className={`group border rounded-lg p-4 transition-all duration-200 ${isCompleted ? 'bg-slate-50 border-slate-200 opacity-60' : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-md'}`} data-id="element-1">
        <div className="flex flex-col gap-4" data-id="element-2">
          {/* Main Content Row */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-3">
            {/* Main Info */}
            <div className="flex items-start gap-4" data-id="element-4">
              <div className={`
                w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold shrink-0
                ${booking.status === 'active' ? 'bg-emerald-100 text-emerald-700' : isCompleted ? 'bg-slate-200 text-slate-400' : 'bg-slate-100 text-slate-500'}
              `} data-id="element-5">
                {getInitials(farmName)}
              </div>

              <div data-id="element-6">
                <h4 className={`font-bold ${isCompleted ? 'text-slate-500' : 'text-slate-900'}`} data-id="element-7">
                  {farmName}
                </h4>
                <div className={`flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm ${isCompleted ? 'text-slate-400' : 'text-slate-500'}`} data-id="element-8">
                  <span className="flex items-center gap-1" data-id="element-9">
                    <User size={14} data-id="element-10" />
                    {booking.userName}
                  </span>
                  <span className="flex items-center gap-1" data-id="element-11">
                    <MapPin size={14} data-id="element-12" />
                    {booking.location}
                  </span>
                </div>
              </div>
            </div>

            {/* Date & Status */}
            <div className="flex items-center justify-between sm:justify-end gap-4 w-full sm:w-auto" data-id="element-13">
              <div className={`flex items-center gap-2 text-sm font-medium px-3 py-1.5 rounded-md ${isCompleted ? 'text-slate-400 bg-slate-100' : 'text-slate-700 bg-slate-50'}`} data-id="element-14">
                <Calendar size={14} className={isCompleted ? 'text-slate-300' : 'text-slate-400'} data-id="element-15" />
                <span data-id="element-16">
                  {formatDate(booking.startDate)} -{' '}
                  {formatDate(booking.endDate)}
                </span>
              </div>

              <span className={`
                px-2.5 py-1 rounded-full text-xs font-semibold uppercase tracking-wide border
                ${statusColors[booking.status]}
              `} data-id="element-17">
                {booking.status}
              </span>
            </div>
          </div>

          {/* Pickup/Dropoff Section - For Upcoming Bookings */}
          {showPickupDropoff && <div className="border-t border-slate-100 pt-3 bg-slate-50/50 -mx-4 px-4 pb-3 rounded-b-lg" data-id="element-18">
              <div className="flex items-start justify-between gap-4" data-id="element-19">
                <div className="flex items-start gap-3 flex-1" data-id="element-20">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 bg-slate-200" data-id="element-21">
                    <Truck size={16} className="text-slate-600" data-id="element-22" />
                  </div>
                  <div className="flex-1" data-id="element-23">
                    <div className="font-semibold text-slate-900 text-sm mb-1" data-id="element-24">
                      Collection & Return
                    </div>
                    <div className="text-sm text-slate-600" data-id="element-25">
                      {booking.pickupMethod === 'farm-pickup' ? `Pickup from ${booking.pickupFrom || 'previous farmer'}` : 'Pickup from Stanthorpe WMF'}
                      {' • '}
                      {booking.dropoffMethod === 'farm-dropoff' ? `Handover to ${formatHandoverText(booking.dropoffTo) || 'next farmer'}` : booking.dropoffMethod === 'wmf-dropoff' ? 'Return to WMF' : 'Dropoff TBD'}
                    </div>
                  </div>
                </div>
                <button onClick={handleViewPickupDropoff} className="text-sm font-medium px-3 py-1.5 rounded-md transition-colors text-slate-700 bg-slate-200 hover:bg-slate-300" data-id="element-26">
                  View Details
                </button>
              </div>
            </div>}

          {/* Next Action Section - For Active Bookings ONLY (not completed) */}
          {showNextAction && <div className={`border-t border-slate-100 pt-3 ${isHandover ? 'bg-blue-50/50' : 'bg-orange-50/50'} -mx-4 px-4 pb-3 rounded-b-lg`} data-id="element-27">
              <div className="flex items-start justify-between gap-4" data-id="element-28">
                <div className="flex items-start gap-3 flex-1" data-id="element-29">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${isHandover ? 'bg-blue-100' : 'bg-orange-100'}`} data-id="element-30">
                    {isHandover ? <Package size={16} className="text-blue-600" data-id="element-31" /> : <Home size={16} className="text-orange-600" data-id="element-32" />}
                  </div>
                  <div className="flex-1" data-id="element-33">
                    <div className="font-semibold text-slate-900 text-sm mb-1" data-id="element-34">
                      {isHandover ? 'Next Action: Handover' : 'Next Action: Return to WMF'}
                    </div>
                    {isHandover ? <div className="text-sm text-slate-600 space-y-0.5" data-id="element-35">
                        <div className="flex items-center gap-1" data-id="element-36">
                          <ArrowRight size={12} className="text-blue-600" data-id="element-37" />
                          <span data-id="element-38">
                            Hand over to{' '}
                            <span className="font-medium" data-id="element-39">
                              {booking.nextFarmerName}
                            </span>
                          </span>
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-40">
                          {booking.nextFarmLocation} •{' '}
                          {formatDate(booking.handoverDate || '')}
                        </div>
                      </div> : <div className="text-sm text-slate-600 space-y-0.5" data-id="element-41">
                        <div className="flex items-center gap-1" data-id="element-42">
                          <ArrowRight size={12} className="text-orange-600" data-id="element-43" />
                          <span data-id="element-44">Return to Stanthorpe WMF</span>
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-45">
                          Deadline: {formatDate(booking.returnDeadline || '')}
                        </div>
                      </div>}
                  </div>
                </div>
                <button onClick={isHandover ? handleViewHandover : handleViewReturn} className={`text-sm font-medium px-3 py-1.5 rounded-md transition-colors ${isHandover ? 'text-blue-700 bg-blue-100 hover:bg-blue-200' : 'text-orange-700 bg-orange-100 hover:bg-orange-200'}`} data-id="element-46">
                  View Details
                </button>
              </div>
            </div>}

          {/* Action Button - Only for active/upcoming bookings (not completed) */}
          {showContactAction && <div className="pt-3 border-t border-slate-100" data-id="element-47">
              <button onClick={handleContactCouncil} className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition-colors" data-id="element-48">
                <Mail size={14} data-id="element-49" />
                <span data-id="element-50">Contact to Change or Cancel Booking</span>
              </button>
            </div>}
        </div>
      </motion.div>

      {/* Modals */}
      {isHandover && <HandoverModal isOpen={isHandoverModalOpen} onClose={() => setIsHandoverModalOpen(false)} booking={booking} data-id="element-51" />}

      {isReturn && <ReturnModal isOpen={isReturnModalOpen} onClose={() => setIsReturnModalOpen(false)} booking={booking} data-id="element-52" />}

      {showPickupDropoff && <PickupDropoffModal isOpen={isPickupDropoffOpen} onClose={() => setIsPickupDropoffOpen(false)} booking={booking} data-id="element-53" />}
    </>;
}
// Pickup/Dropoff Modal - Reads from booking data
function PickupDropoffModal({
  isOpen,
  onClose,
  booking
}: {
  isOpen: boolean;
  onClose: () => void;
  booking: Booking;
}) {
  if (!isOpen) return null;
  // Format handover text to use "from" instead of "at"
  const formatHandoverText = (text: string | undefined) => {
    if (!text) return '';
    return text.replace(' at ', ' from ');
  };
  // Determine pickup details from booking data
  const pickupLocation = booking.pickupMethod === 'farm-pickup' ? booking.pickupFrom || 'Previous farmer (contact details will be provided)' : 'Stanthorpe Waste Management Facility';
  const pickupType = booking.pickupMethod === 'farm-pickup' ? 'Farm Pickup' : 'WMF Collection';
  // Determine dropoff details from booking data
  const dropoffLocation = booking.dropoffMethod === 'farm-dropoff' ? formatHandoverText(booking.dropoffTo) || 'Next farmer will collect from you' : booking.dropoffMethod === 'wmf-dropoff' ? 'Stanthorpe Waste Management Facility' : 'To be determined (depends on next booking)';
  const dropoffType = booking.dropoffMethod === 'farm-dropoff' ? 'Farm Handover' : booking.dropoffMethod === 'wmf-dropoff' ? 'WMF Return' : 'Pending';
  return <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-id="element-54">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 0.95
    }} onClick={e => e.stopPropagation()} className="bg-white rounded-xl shadow-2xl max-w-lg w-full" data-id="element-55">
        {/* Header */}
        <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between rounded-t-xl" data-id="element-56">
          <div className="flex items-center gap-3" data-id="element-57">
            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center" data-id="element-58">
              <Truck className="text-slate-600" size={20} data-id="element-59" />
            </div>
            <div data-id="element-60">
              <h2 className="text-lg font-bold text-slate-900" data-id="element-61">
                Collection & Return Details
              </h2>
              <p className="text-xs text-slate-500" data-id="element-62">
                Pickup and handover information
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors" data-id="element-63">
            <X size={20} data-id="element-64" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6" data-id="element-65">
          {/* Pickup Section */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-66">
            <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2" data-id="element-67">
              <Package size={16} data-id="element-68" />
              Pickup Information
            </h3>
            <div className="space-y-3" data-id="element-69">
              <div className="flex items-start gap-2" data-id="element-70">
                <MapPin size={16} className="text-blue-600 mt-0.5 shrink-0" data-id="element-71" />
                <div data-id="element-72">
                  <div className="font-medium text-slate-900" data-id="element-73">
                    {pickupLocation}
                  </div>
                  <div className="text-sm text-slate-600 mt-1" data-id="element-74">
                    {pickupType}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-2" data-id="element-75">
                <Calendar size={16} className="text-blue-600 mt-0.5 shrink-0" data-id="element-76" />
                <div data-id="element-77">
                  <div className="text-sm text-slate-600" data-id="element-78">
                    <span className="font-medium text-slate-900" data-id="element-79">
                      Pickup Date:
                    </span>{' '}
                    {booking.startDate}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Dropoff/Handover Section */}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4" data-id="element-80">
            <h3 className="font-semibold text-orange-900 mb-3 flex items-center gap-2" data-id="element-81">
              <Home size={16} data-id="element-82" />
              {booking.dropoffMethod === 'farm-dropoff' ? 'Handover Information' : 'Return Information'}
            </h3>
            <div className="space-y-3" data-id="element-83">
              <div className="flex items-start gap-2" data-id="element-84">
                <MapPin size={16} className="text-orange-600 mt-0.5 shrink-0" data-id="element-85" />
                <div data-id="element-86">
                  <div className="font-medium text-slate-900" data-id="element-87">
                    {dropoffLocation}
                  </div>
                  <div className="text-sm text-slate-600 mt-1" data-id="element-88">
                    {dropoffType}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-2" data-id="element-89">
                <Calendar size={16} className="text-orange-600 mt-0.5 shrink-0" data-id="element-90" />
                <div data-id="element-91">
                  <div className="text-sm text-slate-600" data-id="element-92">
                    <span className="font-medium text-slate-900" data-id="element-93">
                      {booking.dropoffMethod === 'farm-dropoff' ? 'Handover Date:' : 'Return Date:'}
                    </span>{' '}
                    {booking.endDate}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Important Notes */}
          {booking.pickupMethod === 'farm-pickup' && <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-94">
              <h4 className="font-semibold text-blue-900 mb-2 text-sm" data-id="element-95">
                Farm Pickup Notes:
              </h4>
              <ul className="space-y-1.5 text-sm text-blue-800" data-id="element-96">
                <li className="flex items-start gap-2" data-id="element-97">
                  <span className="text-blue-400 mt-0.5" data-id="element-98">•</span>
                  <span data-id="element-99">
                    You will be contacted by the previous farmer to coordinate
                    pickup
                  </span>
                </li>
                <li className="flex items-start gap-2" data-id="element-100">
                  <span className="text-blue-400 mt-0.5" data-id="element-101">•</span>
                  <span data-id="element-102">Arrange a convenient time for collection</span>
                </li>
              </ul>
            </div>}

          {booking.dropoffMethod === 'farm-dropoff' && <div className="bg-orange-50 border border-orange-200 rounded-lg p-4" data-id="element-103">
              <h4 className="font-semibold text-orange-900 mb-2 text-sm" data-id="element-104">
                Handover Notes:
              </h4>
              <ul className="space-y-1.5 text-sm text-orange-800" data-id="element-105">
                <li className="flex items-start gap-2" data-id="element-106">
                  <span className="text-orange-400 mt-0.5" data-id="element-107">•</span>
                  <span data-id="element-108">
                    The next farmer will contact you to arrange collection
                  </span>
                </li>
                <li className="flex items-start gap-2" data-id="element-109">
                  <span className="text-orange-400 mt-0.5" data-id="element-110">•</span>
                  <span data-id="element-111">
                    Wait at your farm for them to pick up the equipment
                  </span>
                </li>
              </ul>
            </div>}

          {booking.dropoffMethod === 'pending' && <div className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-id="element-112">
              <h4 className="font-semibold text-slate-900 mb-2 text-sm" data-id="element-113">
                Dropoff To Be Confirmed:
              </h4>
              <p className="text-sm text-slate-700" data-id="element-114">
                Your dropoff method will be determined based on the next
                booking. You'll be notified if:
              </p>
              <ul className="mt-2 space-y-1.5 text-sm text-slate-700" data-id="element-115">
                <li className="flex items-start gap-2" data-id="element-116">
                  <span className="text-slate-400 mt-0.5" data-id="element-117">•</span>
                  <span data-id="element-118">
                    Next farmer will collect from your farm (handover)
                  </span>
                </li>
                <li className="flex items-start gap-2" data-id="element-119">
                  <span className="text-slate-400 mt-0.5" data-id="element-120">•</span>
                  <span data-id="element-121">You need to return to Stanthorpe WMF</span>
                </li>
              </ul>
            </div>}

          {/* Contact Info */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-id="element-122">
            <h4 className="font-semibold text-slate-900 mb-2 text-sm" data-id="element-123">
              Need Help?
            </h4>
            <p className="text-sm text-slate-700" data-id="element-124">
              Contact us at <strong data-id="element-125">1300 697 372</strong> or{' '}
              <strong data-id="element-126">0451 982 442</strong> for coordination or any questions.
            </p>
          </div>

          {/* Close Button */}
          <button onClick={onClose} className="w-full px-4 py-2.5 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 transition-colors" data-id="element-127">
            Close
          </button>
        </div>
      </motion.div>
    </div>;
}